import React, { useState, useEffect, useRef } from "react";
import { Loader, Trash, Download, Folder, FolderPlus, FolderInput, Pencil, Upload, ChevronRight } from "lucide-react";
import { toast } from "react-toastify";
import axios from "axios";
import {
  uploadCustomerFile,
  getCustomerStorage,
  deleteCustomerFile,
  getCustomerFileDownloadUrl,
  getCustomerFolders,
  createCustomerFolder,
  renameCustomerFolder,
  deleteCustomerFolder,
  moveCustomerFile,
} from "../api/Content.api.js";
import {
  joinPath,
  getBreadcrumbs,
  getChildFolders,
  getFilesInPath,
  getFolderOptions,
  validateFolderName,
} from "../utils/contentFolders.js";
import { formatDateTime24 } from "../../utils/formatTime24";

interface CustomerFile {
  id: string;
  originalName: string;
  s3Key: string;
  mimeType: string;
  sizeInBytes: number;
  uploadedBy: string;
  uploadedAt: string;
  downloadCount: number;
  maxDownloads: number;
  folderPath?: string;
}

interface ContentFolder {
  id: string;
  path: string;
}

// Folder dialog: create inside currentPath, or rename the folder at `path`.
type FolderDialog = { mode: "create" } | { mode: "rename"; path: string };

interface CustomerContentProps {
  isOpen: boolean;
  onClose: () => void;
  customerId: string;
}

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

const formatBytes = (bytes: number): string => {
  if (!bytes || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(2)} ${units[i]}`;
};

const formatDate = (dateString: string): string => {
  if (!dateString) return "N/A";
  try {
    return formatDateTime24(dateString, { seconds: true });
  } catch {
    return dateString;
  }
};

const leafName = (folder: ContentFolder): string => folder.path.slice(folder.path.lastIndexOf("/") + 1);

const CustomerContent = ({ isOpen, onClose, customerId }: CustomerContentProps) => {
  const [files, setFiles] = useState<CustomerFile[]>([]);
  const [totalSizeGB, setTotalSizeGB] = useState(0);
  const [maxLimitGB, setMaxLimitGB] = useState(20.0);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<CustomerFile | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [folders, setFolders] = useState<ContentFolder[]>([]);
  const [currentPath, setCurrentPath] = useState("");
  const [folderDialog, setFolderDialog] = useState<FolderDialog | null>(null);
  const [folderName, setFolderName] = useState("");
  const [folderToDelete, setFolderToDelete] = useState<ContentFolder | null>(null);
  const [fileToMove, setFileToMove] = useState<CustomerFile | null>(null);
  const [moveTarget, setMoveTarget] = useState("");

  const showApiError = (error: unknown) => {
    console.error(error);
    if (axios.isAxiosError(error) && error.response) {
      if (error.response.status === 403) {
        toast.error("No tenés permiso para realizar esta acción.", { autoClose: 10000 });
      } else {
        toast.error(error.response.data?.error || error.response.data?.message || "Error desconocido", {
          autoClose: 10000,
        });
      }
    } else {
      toast.error((error as Error).message || "Error desconocido");
    }
  };

  const fetchStorage = async () => {
    if (!customerId) return;
    setIsLoading(true);
    try {
      const [response, folderList] = await Promise.all([
        getCustomerStorage(customerId),
        getCustomerFolders(customerId),
      ]);
      setFolders(folderList || []);
      setFiles(response.files || []);
      setTotalSizeGB(response.totalSizeGB || 0);
      setMaxLimitGB(response.maxLimitGB || 20.0);
    } catch (error) {
      console.error(error);
      if (axios.isAxiosError(error) && error.response) {
        if (error.response.status === 403) {
          toast.error("No tenés permiso para realizar esta acción.", { autoClose: 10000 });
        } else {
          const toastId = toast.error(error.response.data.error || error.response.data.message || "Error desconocido", {
            autoClose: 10000,
          });
          setTimeout(() => toast.dismiss(toastId), 20000);
        }
      } else {
        toast.error((error as Error).message || "Error desconocido");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && customerId) {
      setCurrentPath("");
      fetchStorage();
    }
  }, [isOpen, customerId]);

  // If the current folder disappears (e.g. renamed elsewhere), fall back to the root.
  useEffect(() => {
    if (currentPath && !folders.some((f) => f.path === currentPath)) {
      setCurrentPath("");
    }
  }, [folders, currentPath]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      toast.error(
        `El archivo es demasiado grande. Máximo: 100MB, Tu archivo: ${(file.size / (1024 * 1024)).toFixed(2)}MB`,
        { autoClose: 10000 }
      );
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setIsUploading(true);
    try {
      await uploadCustomerFile(customerId, file, currentPath);
      toast.success("Archivo subido correctamente.");
      await fetchStorage();
    } catch (error) {
      console.error(error);
      if (axios.isAxiosError(error) && error.response) {
        if (error.response.status === 403) {
          toast.error("No tenés permiso para realizar esta acción.", { autoClose: 10000 });
        } else if (error.response.status === 413) {
          toast.error(
            "Límite de almacenamiento excedido (20 GB máximos). Borrá algún archivo para liberar espacio.",
            { autoClose: 10000 }
          );
        } else {
          const toastId = toast.error(error.response.data.error || error.response.data.message || "Error desconocido", {
            autoClose: 10000,
          });
          setTimeout(() => toast.dismiss(toastId), 20000);
        }
      } else {
        toast.error((error as Error).message || "Error desconocido");
      }
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDelete = (file: CustomerFile) => {
    setFileToDelete(file);
    setIsDeleteModalOpen(true);
  };

  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
    setFileToDelete(null);
  };

  const confirmDelete = async () => {
    if (!fileToDelete) {
      setIsDeleteModalOpen(false);
      return;
    }

    try {
      await deleteCustomerFile(fileToDelete.id);
      toast.success("Archivo eliminado correctamente");
      setIsDeleteModalOpen(false);
      setFileToDelete(null);
      await fetchStorage();
    } catch (error) {
      console.error(error);
      if (axios.isAxiosError(error) && error.response) {
        if (error.response.status === 403) {
          toast.error("No tenés permiso para realizar esta acción.", { autoClose: 10000 });
        } else {
          const toastId = toast.error(error.response.data.error || error.response.data.message || "Error desconocido", {
            autoClose: 10000,
          });
          setTimeout(() => toast.dismiss(toastId), 20000);
        }
      } else {
        toast.error((error as Error).message || "Error desconocido");
      }
    }
  };

  const handleDownload = async (file: CustomerFile) => {
    try {
      const response = await getCustomerFileDownloadUrl(file.id);
      window.open(response.url, "_blank");
      setFiles((prev) =>
        prev.map((f) =>
          f.id === file.id ? { ...f, downloadCount: response.downloadCount } : f
        )
      );
    } catch (error) {
      console.error(error);
      if (axios.isAxiosError(error) && error.response) {
        if (error.response.status === 403) {
          toast.error("No tenés permiso para realizar esta acción.", { autoClose: 10000 });
        } else if (error.response.status === 429) {
          toast.error(
            error.response.data.error || "Se alcanzó el límite de 10 descargas para este archivo.",
            { autoClose: 10000 }
          );
        } else {
          const toastId = toast.error(error.response.data.error || error.response.data.message || "Error desconocido", {
            autoClose: 10000,
          });
          setTimeout(() => toast.dismiss(toastId), 20000);
        }
      } else {
        toast.error((error as Error).message || "Error desconocido");
      }
    }
  };

  const openCreateFolder = () => {
    setFolderName("");
    setFolderDialog({ mode: "create" });
  };

  const openRenameFolder = (folder: ContentFolder) => {
    setFolderName(leafName(folder));
    setFolderDialog({ mode: "rename", path: folder.path });
  };

  const submitFolderDialog = async () => {
    if (!folderDialog) return;
    const slash = folderDialog.mode === "rename" ? folderDialog.path.lastIndexOf("/") : -1;
    const parentPath = folderDialog.mode === "create" ? currentPath : slash < 0 ? "" : folderDialog.path.slice(0, slash);
    const validation = validateFolderName(folderName, parentPath);
    if (!validation.ok) {
      toast.error(validation.error);
      return;
    }
    try {
      if (folderDialog.mode === "create") {
        await createCustomerFolder(customerId, joinPath(currentPath, validation.name));
        toast.success("Carpeta creada correctamente");
      } else {
        await renameCustomerFolder(customerId, folderDialog.path, validation.name);
        // Keep the user inside the renamed folder if they were in it or below it.
        const newPath = joinPath(parentPath, validation.name);
        if (currentPath === folderDialog.path || currentPath.startsWith(`${folderDialog.path}/`)) {
          setCurrentPath(newPath + currentPath.slice(folderDialog.path.length));
        }
        toast.success("Carpeta renombrada correctamente");
      }
      setFolderDialog(null);
      await fetchStorage();
    } catch (error) {
      showApiError(error);
    }
  };

  const confirmDeleteFolder = async () => {
    if (!folderToDelete) return;
    try {
      await deleteCustomerFolder(customerId, folderToDelete.path);
      toast.success("Carpeta eliminada correctamente");
      setFolderToDelete(null);
      await fetchStorage();
    } catch (error) {
      // 409 carries the "not empty" message from the backend.
      showApiError(error);
      setFolderToDelete(null);
    }
  };

  const openMoveFile = (file: CustomerFile) => {
    setFileToMove(file);
    setMoveTarget(file.folderPath || "");
  };

  const confirmMoveFile = async () => {
    if (!fileToMove) return;
    try {
      await moveCustomerFile(fileToMove.id, moveTarget);
      toast.success("Archivo movido correctamente");
      setFileToMove(null);
      await fetchStorage();
    } catch (error) {
      showApiError(error);
    }
  };

  if (!isOpen) return null;

  const breadcrumbs = getBreadcrumbs(currentPath);
  const childFolders = getChildFolders(folders, currentPath);
  const visibleFiles = getFilesInPath(files, currentPath);

  const quotaPercent = Math.min((totalSizeGB / maxLimitGB) * 100, 100);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden transition-all duration-300">
        <button
          onClick={onClose}
          className="absolute right-4 top-2 text-gray-400 hover:text-gray-500 z-20"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="p-4 md:p-6 flex-grow overflow-y-auto">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-6">
            Contenido
          </h2>

          {/* Storage quota bar */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm font-medium text-gray-700">Almacenamiento utilizado</span>
              <span className="text-sm text-gray-600">
                {totalSizeGB.toFixed(2)} GB / {maxLimitGB.toFixed(1)} GB
              </span>
            </div>
            <div className="h-3 w-full bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ease-in-out ${
                  quotaPercent >= 90 ? "bg-red-500" : quotaPercent >= 70 ? "bg-yellow-500" : "bg-blue-500"
                }`}
                style={{ width: `${quotaPercent}%` }}
              />
            </div>
          </div>

          {/* Breadcrumbs */}
          <nav className="flex flex-wrap items-center gap-1 mb-3 text-sm" aria-label="Ruta de carpetas">
            {breadcrumbs.map((crumb, i) => (
              <React.Fragment key={crumb.path}>
                {i > 0 && <ChevronRight size={14} className="text-gray-400" />}
                {i === breadcrumbs.length - 1 ? (
                  <span className="font-semibold text-gray-800">{crumb.name}</span>
                ) : (
                  <button
                    onClick={() => setCurrentPath(crumb.path)}
                    className="text-blue-600 hover:underline"
                  >
                    {crumb.name}
                  </button>
                )}
              </React.Fragment>
            ))}
          </nav>

          {/* Actions */}
          <div className="mb-6">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={openCreateFolder}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md text-sm font-semibold hover:bg-gray-200 transition"
              >
                <FolderPlus size={16} />
                Nueva carpeta
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-md text-sm font-semibold hover:bg-blue-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Upload size={16} />
                Subir
              </button>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileChange}
                disabled={isUploading}
                className="hidden"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">Máximo 100MB por archivo.</p>
            {isUploading && (
              <div className="flex items-center gap-2 mt-2 text-sm text-blue-600">
                <Loader size={16} className="animate-spin" />
                Subiendo archivo...
              </div>
            )}
          </div>

          {/* File list */}
          {isLoading ? (
            <div className="flex justify-center items-center h-full min-h-[200px]">
              <Loader size={40} className="animate-spin text-blue-500" />
            </div>
          ) : childFolders.length === 0 && visibleFiles.length === 0 ? (
            <div className="flex justify-center items-center h-full min-h-[200px]">
              <p className="text-gray-500 text-lg">
                {currentPath ? "Esta carpeta está vacía" : "No hay archivos subidos todavía"}
              </p>
            </div>
          ) : (
            <>
              {/* Mobile Card Layout */}
              <div className="md:hidden space-y-3">
                {childFolders.map((folder) => (
                  <div key={folder.path} className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm flex items-center justify-between">
                    <button
                      onClick={() => setCurrentPath(folder.path)}
                      className="flex items-center gap-2 flex-1 min-w-0 text-left text-sm font-semibold text-gray-900"
                    >
                      <Folder className="w-5 h-5 text-yellow-500 shrink-0" />
                      <span className="truncate">{leafName(folder)}</span>
                    </button>
                    <div className="flex gap-2 ml-2">
                      <button
                        onClick={() => openRenameFolder(folder)}
                        className="min-w-[44px] min-h-[44px] flex items-center justify-center p-2 rounded-full bg-gray-100 text-gray-600 active:bg-gray-200 transition duration-150 touch-manipulation"
                        aria-label="Renombrar carpeta"
                      >
                        <Pencil className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => setFolderToDelete(folder)}
                        className="min-w-[44px] min-h-[44px] flex items-center justify-center p-2 rounded-full bg-red-100 text-red-600 active:bg-red-200 transition duration-150 touch-manipulation"
                        aria-label="Eliminar carpeta"
                      >
                        <Trash className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
                {visibleFiles.map((f) => (
                  <div key={f.id} className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-gray-900 mb-1 truncate">{f.originalName}</h3>
                        <p className="text-xs text-gray-600">{formatBytes(f.sizeInBytes)}</p>
                        <p className="text-xs text-gray-500">{formatDate(f.uploadedAt)}</p>
                        <p className="text-xs text-gray-500">
                          Descargas: {f.downloadCount}/{f.maxDownloads}
                        </p>
                      </div>
                      <div className="flex gap-2 ml-2">
                        <button
                          onClick={() => openMoveFile(f)}
                          className="min-w-[44px] min-h-[44px] flex items-center justify-center p-2 rounded-full bg-gray-100 text-gray-600 active:bg-gray-200 transition duration-150 touch-manipulation"
                          aria-label="Mover archivo"
                        >
                          <FolderInput className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDownload(f)}
                          className="min-w-[44px] min-h-[44px] flex items-center justify-center p-2 rounded-full bg-blue-100 text-blue-600 active:bg-blue-200 transition duration-150 touch-manipulation"
                          aria-label="Descargar archivo"
                        >
                          <Download className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(f)}
                          className="min-w-[44px] min-h-[44px] flex items-center justify-center p-2 rounded-full bg-red-100 text-red-600 active:bg-red-200 transition duration-150 touch-manipulation"
                          aria-label="Eliminar archivo"
                        >
                          <Trash className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table Layout */}
              <div className="hidden md:block overflow-x-auto bg-white shadow-lg rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Nombre
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Tamaño
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Fecha
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Descargas
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {childFolders.map((folder) => (
                      <tr key={folder.path} className="hover:bg-gray-50 transition-colors">
                        <td colSpan={4} className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 max-w-xs truncate">
                          <button
                            onClick={() => setCurrentPath(folder.path)}
                            className="flex items-center gap-2 hover:underline"
                          >
                            <Folder className="w-5 h-5 text-yellow-500" />
                            {leafName(folder)}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                          <div className="flex justify-center space-x-2">
                            <button
                              onClick={() => openRenameFolder(folder)}
                              className="p-3 lg:p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition duration-150"
                              title="Renombrar carpeta"
                            >
                              <Pencil className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => setFolderToDelete(folder)}
                              className="p-3 lg:p-2 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition duration-150"
                              title="Eliminar carpeta"
                            >
                              <Trash className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {visibleFiles.map((f) => (
                      <tr key={f.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 max-w-xs truncate">
                          {f.originalName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {formatBytes(f.sizeInBytes)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {formatDate(f.uploadedAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600">
                          {f.downloadCount}/{f.maxDownloads}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                          <div className="flex justify-center space-x-2">
                            <button
                              onClick={() => openMoveFile(f)}
                              className="p-3 lg:p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition duration-150"
                              title="Mover"
                            >
                              <FolderInput className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDownload(f)}
                              className="p-3 lg:p-2 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition duration-150"
                              title="Descargar"
                            >
                              <Download className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(f)}
                              className="p-3 lg:p-2 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition duration-150"
                              title="Eliminar"
                            >
                              <Trash className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
          >
            Cerrar
          </button>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {isDeleteModalOpen && fileToDelete && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && cancelDelete()}
        >
          <div className="bg-black bg-opacity-40 absolute inset-0"></div>
          <div className="relative bg-white rounded-lg shadow-lg w-full max-w-md p-6 z-60">
            <h3 className="text-lg font-semibold mb-2">Confirmar eliminación</h3>
            <p className="text-sm text-gray-700 mb-6">
              ¿Confirmas eliminar el archivo <span className="font-medium">{fileToDelete.originalName}</span>?
            </p>
            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <button
                onClick={cancelDelete}
                className="w-full sm:w-auto px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 active:bg-gray-400 transition min-h-[44px] touch-manipulation"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                className="w-full sm:w-auto px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 active:bg-red-800 transition min-h-[44px] touch-manipulation"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create / rename folder modal */}
      {folderDialog && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && setFolderDialog(null)}
        >
          <div className="bg-black bg-opacity-40 absolute inset-0"></div>
          <div className="relative bg-white rounded-lg shadow-lg w-full max-w-md p-6 z-60">
            <h3 className="text-lg font-semibold mb-4">
              {folderDialog.mode === "create" ? "Nueva carpeta" : "Renombrar carpeta"}
            </h3>
            <input
              autoFocus
              type="text"
              value={folderName}
              maxLength={100}
              onChange={(e) => setFolderName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitFolderDialog()}
              placeholder="Nombre de la carpeta"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-6"
            />
            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <button
                onClick={() => setFolderDialog(null)}
                className="w-full sm:w-auto px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 active:bg-gray-400 transition min-h-[44px] touch-manipulation"
              >
                Cancelar
              </button>
              <button
                onClick={submitFolderDialog}
                className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition min-h-[44px] touch-manipulation"
              >
                {folderDialog.mode === "create" ? "Crear" : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete folder confirmation modal */}
      {folderToDelete && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && setFolderToDelete(null)}
        >
          <div className="bg-black bg-opacity-40 absolute inset-0"></div>
          <div className="relative bg-white rounded-lg shadow-lg w-full max-w-md p-6 z-60">
            <h3 className="text-lg font-semibold mb-2">Confirmar eliminación</h3>
            <p className="text-sm text-gray-700 mb-6">
              ¿Confirmas eliminar la carpeta <span className="font-medium">{leafName(folderToDelete)}</span>? Solo se puede eliminar si está vacía.
            </p>
            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <button
                onClick={() => setFolderToDelete(null)}
                className="w-full sm:w-auto px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 active:bg-gray-400 transition min-h-[44px] touch-manipulation"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDeleteFolder}
                className="w-full sm:w-auto px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 active:bg-red-800 transition min-h-[44px] touch-manipulation"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move file modal */}
      {fileToMove && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && setFileToMove(null)}
        >
          <div className="bg-black bg-opacity-40 absolute inset-0"></div>
          <div className="relative bg-white rounded-lg shadow-lg w-full max-w-md p-6 z-60">
            <h3 className="text-lg font-semibold mb-2">Mover archivo</h3>
            <p className="text-sm text-gray-700 mb-4">
              Elegí la carpeta de destino para <span className="font-medium">{fileToMove.originalName}</span>.
            </p>
            <select
              value={moveTarget}
              onChange={(e) => setMoveTarget(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-6"
            >
              {getFolderOptions(folders).map((option) => (
                <option key={option.path} value={option.path}>
                  {option.label}
                </option>
              ))}
            </select>
            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <button
                onClick={() => setFileToMove(null)}
                className="w-full sm:w-auto px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 active:bg-gray-400 transition min-h-[44px] touch-manipulation"
              >
                Cancelar
              </button>
              <button
                onClick={confirmMoveFile}
                disabled={moveTarget === (fileToMove.folderPath || "")}
                className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition min-h-[44px] touch-manipulation disabled:opacity-50"
              >
                Mover
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerContent;
